import type React from 'react';
import { memo, useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  getInputColors,
  INPUT_MULTILINE_HEIGHT,
  INPUT_PADDING_HORIZONTAL,
} from '@wecasa/common/src/components/input/styles';

import type { WithClassNameProps } from '../types';
import { StylelessTextarea } from '../utils';
import { baseInputStyle, Container, InputContainer, InputFrame, type InputStyleProps } from './components';
import AnimatedPlaceholder from './components/animated-placeholder';
import BottomMessage from './components/bottom-message';
import InputIcon from './components/input-icon';

const StyledInputContainer = styled(InputContainer)`
  position: relative;
  flex-grow: 1;
  padding-right: 0;
`;

const StyledTextarea = styled(StylelessTextarea)<InputStyleProps>`
  ${baseInputStyle};
  min-height: ${INPUT_MULTILINE_HEIGHT}px;
  height: 100%;
`;

const StyledInputIcon = styled(InputIcon)`
  position: absolute;
  top: ${INPUT_PADDING_HORIZONTAL}px;
  right: 0;
`;

export type TextareaProps = WithClassNameProps & {
  name: string;
  label: string;
  value?: string;
  id: string;
  iconName?: string;
  placeholder?: string;
  error?: string;
  message?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  disabled?: boolean;
};

const Textarea = ({
  name,
  label,
  id,
  value,
  iconName,
  placeholder,
  error,
  message,
  onBlur,
  onFocus,
  onChange,
  className,
  rows,
  disabled = false,
}: TextareaProps) => {
  const hasLabel = !!label;
  const hasValue = !!value;
  const [isFocused, setIsFocused] = useState(false);

  const inputColors = getInputColors({ error, hasValue, isFocused });

  const onInputBlur = useCallback(() => {
    setIsFocused(false);
    if (onBlur) onBlur();
  }, [onBlur]);

  const onInputFocus = useCallback(() => {
    setIsFocused(true);
    if (onFocus) onFocus();
  }, [onFocus]);

  const textareaProps = {
    $hasLabel: hasLabel,
    $isFocused: isFocused,
    'aria-label': label,
    $margin: 15,
    disabled,
    error,
    id,
    label,
    name,
    onBlur: onInputBlur,
    onChange,
    onFocus: onInputFocus,
    placeholder: placeholder || ' ',
    rows,
    value,
  };

  return (
    <Container $isDisabled={disabled} className={className}>
      <InputFrame $inputId={id} $hasHoverStyle={!disabled && !isFocused && !error} $inputColors={inputColors}>
        <StyledInputContainer>
          <StyledTextarea {...textareaProps} />
          {hasLabel && (
            <AnimatedPlaceholder
              color={inputColors.placeholder}
              inputId={id}
              isActive={isFocused || !!value}
              label={label}
            />
          )}
        </StyledInputContainer>
        {iconName && <StyledInputIcon color={inputColors.icon} inputId={id} name={iconName} />}
      </InputFrame>
      <BottomMessage error={error} message={message} />
    </Container>
  );
};

export default memo(Textarea);
